import { BaseExtension, ViewerEvents } from '@immersive-tech/viewer-engine';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { PerspectiveEvents } from './events';

import type { Viewer, Extension } from '@immersive-tech/viewer-engine';
import type { ManifestHandler } from '@/viewerExtensions';
import type { ManifestViewZoomRestriction } from '@/types/IManifest';
import type { IExtendedArcRotateCamera } from '@/types/IExtendedArcRotateCamera';

export class Perspectives extends BaseExtension {
  private cameraPosition: Vector3;
  private cameraTarget: Vector3;
  public activePerspective: string;

  constructor(viewer: Viewer) {
    super(viewer);

    this.cameraPosition = Vector3.Zero();
    this.cameraTarget = Vector3.Zero();
    this.activePerspective = 'Main';

    this.viewer.addEventListener(ViewerEvents.VIEWER_INITIALIZED, this.viewerInitializedEventHandler.bind(this));
  }

  protected get ManifestHandlerExtension() {
    return this.viewer.extensions.get('ManifestHandler') as Extension<ManifestHandler>;
  }

  get sceneJson() {
    return this.ManifestHandlerExtension.sceneJson;
  }

  get perspectives() {
    return this.sceneJson?.views;
  }

  private viewerInitializedEventHandler() {
    if (!this.viewer.scene) return;

    this.viewer.scene.onAfterCameraRenderObservable.add(this.cameraChangeEvent.bind(this));
  }

  private cameraChangeEvent() {
    if (!this.viewer.camera) return;

    if (!this.cameraPosition.equals(this.viewer.camera.position)) {
      this.cameraPosition = this.viewer.camera.position.clone();

      this.viewer.dispatchViewerEvent(PerspectiveEvents.CAMERA_POSITION_CHANGE, {
        cameraPosition: this.cameraPosition,
      });
    }

    if (!this.cameraTarget.equals(this.viewer.camera.target)) {
      this.cameraTarget = this.viewer.camera.target.clone();

      this.viewer.dispatchViewerEvent(PerspectiveEvents.CAMERA_TARGET_CHANGE, {
        cameraTarget: this.cameraTarget,
      });
    }
  }

  public setZoomRestriction([lowerLimit, upperLimit]: ManifestViewZoomRestriction = [0, 10]) {
    if (!this.viewer.camera) return;

    this.viewer.camera.lowerRadiusLimit = lowerLimit;
    this.viewer.camera.upperRadiusLimit = upperLimit;

    this.viewer.renderNextFrame += 1;

    this.viewer.dispatchViewerEvent(PerspectiveEvents.ZOOM_RESTRICTION_SET, {
      lowerRadiusLimit: this.viewer.camera.lowerRadiusLimit,
      upperRadiusLimit: this.viewer.camera.upperRadiusLimit,
    });
  }

  public resetZoomRestriction() {
    if (!this.viewer.camera) return;

    this.viewer.camera.lowerRadiusLimit = 0;
    this.viewer.camera.upperRadiusLimit = null;

    this.viewer.renderNextFrame += 1;

    this.viewer.dispatchViewerEvent(PerspectiveEvents.ZOOM_RESTRICTION_SET, {
      lowerRadiusLimit: this.viewer.camera.lowerRadiusLimit,
      upperRadiusLimit: this.viewer.camera.upperRadiusLimit,
    });
  }

  public setAngleRestriction(angle = 0) {
    if (!this.viewer.camera) return;

    const camera = this.viewer.camera as IExtendedArcRotateCamera;
    const restrictAngleRadians = angle * (Math.PI / 180);

    camera.lowerAlphaLimit = (camera.baseAlpha ?? camera.alpha) - restrictAngleRadians;
    camera.lowerBetaLimit = (camera.baseBeta ?? camera.beta) - restrictAngleRadians;
    camera.upperAlphaLimit = (camera.baseAlpha ?? camera.alpha) + restrictAngleRadians;
    camera.upperBetaLimit = (camera.baseBeta ?? camera.beta) + restrictAngleRadians;

    this.viewer.renderNextFrame += 1;

    this.viewer.dispatchViewerEvent(PerspectiveEvents.ANGLE_RESTRICTION_SET, {
      lowerAlphaLimit: camera.lowerAlphaLimit,
      upperAlphaLimit: camera.upperAlphaLimit,
      lowerBetaLimit: camera.lowerBetaLimit,
      upperBetaLimit: camera.upperBetaLimit,
    });
  }

  public resetAngleRestriction() {
    if (!this.viewer.camera) return;

    const { camera } = this.viewer;

    camera.lowerAlphaLimit = null;
    camera.upperAlphaLimit = null;
    camera.lowerBetaLimit = null;
    camera.upperBetaLimit = null;

    this.viewer.dispatchViewerEvent(PerspectiveEvents.ANGLE_RESTRICTION_SET, {
      lowerAlphaLimit: camera.lowerAlphaLimit,
      upperAlphaLimit: camera.upperAlphaLimit,
      lowerBetaLimit: camera.lowerBetaLimit,
      upperBetaLimit: camera.upperBetaLimit,
    });
  }

  public setBaseRotation() {
    if (!this.viewer.camera) return;

    const camera = this.viewer.camera as IExtendedArcRotateCamera;
    camera.baseAlpha = camera.alpha;
    camera.baseBeta = camera.beta;
  }

  public resetBaseRotation() {
    if (!this.viewer.camera) return;

    const camera = this.viewer.camera as IExtendedArcRotateCamera;
    camera.baseAlpha = null;
    camera.baseBeta = null;
  }

  public async setPerspective(perspectiveName: string) {
    if (!this.viewer.camera) return;

    const perspective = this.sceneJson?.views[perspectiveName];

    if (!perspective) return;

    this.viewer.dispatchViewerEvent(PerspectiveEvents.PERSPECTIVE_CHANGE_START, {
      perspectiveName,
    });

    this.viewer.camera.detachControl();

    this.resetZoomRestriction();
    this.resetAngleRestriction();

    await this.viewer.interpolateCamera(perspective.position, perspective.target);

    this.viewer.camera.attachControl(this.viewer.canvasElement, false);

    this.viewer.dispatchViewerEvent(PerspectiveEvents.PERSPECTIVE_CHANGE_END, {
      perspectiveName,
    });
  }

  public resetView() {
    this.setPerspective('Main');

    this.viewer.dispatchViewerEvent(PerspectiveEvents.RESET_VIEW);
  }
}
